import { z } from "zod";

// ---------------------------------------------------------------------------
// 振込口座（043、docs/32、#79）。設定 → 振込口座で複数登録し、既定を1つ決める。請求書ごとに選び直せる。
// 銀行名・支店名・コード・種別は平文、口座番号・名義は暗号化（CLAUDE.md ルール5）
// ---------------------------------------------------------------------------

export const BANK_MSG = {
  bankNameRequired: "銀行名を入れてください（銀行コードを入れると自動で入ります）",
  branchNameRequired: "支店名を入れてください（支店コードを入れると自動で入ります）",
  accountNoRequired: "口座番号を入れてください",
  accountNoInvalid: "口座番号は数字で入れてください（7桁が多いです）",
  holderRequired: "口座名義を入れてください（通帳のカナのとおり）",
  bankCodeInvalid: "銀行コードは4桁の数字です",
  branchCodeInvalid: "支店コードは3桁の数字です",
  notFound: "その振込口座は見つかりません",
  // #155（docs/64 §3）：一覧の行の「既定にする」はやめ、口座のシートの「既定の口座」のスイッチへ。修正のシートの削除の行の注もこの文
  defaultInUse: "既定の口座は削除できません。先にほかの口座を既定にしてください",
  bankNotFound: "その銀行コードは見つかりませんでした。銀行名を直接入れてください",
  branchNotFound: "その支店コードは見つかりませんでした。支店名を直接入れてください",
  saved: "振込口座を保存しました",
  deleted: "振込口座を消しました",
  madeDefault: "既定の口座にしました",
  adminOnly: "振込口座を変えられるのは管理者だけです",
  // ---- 画面（#155、docs/64 §2・§3、Figma「振込口座（10/9）」⑪〜⑯）----
  title: "振込口座",
  add: "追加",
  addTitle: "口座追加",
  editTitle: "口座修正",
  listNote: "請求書には既定の口座が入ります。2つ以上あれば請求書ごとに選べます",
  sectionPrint: "紙の印字（既定の口座）",
  noneTitle: "振込口座はまだありません",
  noneSub: "空のままでも請求書は出せます",
  sectionLegacy: "以前の振込先",
  legacyAction: "この内容で登録",
  legacyNote: "欄に分けて登録すると、この行は消えます",
  sectionBank: "銀行",
  sectionBranch: "支店",
  sectionAccount: "口座",
  bankCode: "銀行コード",
  bankName: "銀行名",
  branchCode: "支店コード",
  branchName: "支店名",
  accountType: "種別",
  accountNo: "口座番号",
  holder: "口座名義",
  holderNote: "通帳のカナのとおり",
  /** 候補が出ているときの節の注（Figma ⑭） */
  candHint: "↓↑ で選んで Enter、または押すとコードも入ります",
  /** 名前だけでコードが無いときの節の注（Figma ⑮） */
  nameOnlyNote: "コードが分からなければ名前だけで登録できます",
  bankCands: "銀行の候補",
  branchCands: "支店の候補",
  defaultLabel: "既定の口座",
  firstDefault: "最初の口座なので、既定の口座になります",
  preview: "紙には「{text}」と印字されます",
  delete: "口座削除",
  deleteAsk: "この口座を削除しますか",
  deleteBody: "消すと、下書きの請求書の振込先は既定の口座に戻ります。出したあとの請求書の振込先は変わりません。元に戻せません。",
} as const;

const toHalf = (s: string) => s.replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0));
/** 数字だけに（全角も半角に） */
export const digitsOnly = (s: string): string => toHalf(s).replace(/[^0-9]/g, "");

export const BankAccountType = z.enum(["ordinary", "current"]);
export type BankAccountType = z.infer<typeof BankAccountType>;
export const BANK_TYPE_LABEL: Record<BankAccountType, string> = { ordinary: "普通", current: "当座" };

/** 紙に印字する中身（銀行・支店・コード・種別・口座番号・名義）。出した時点で JSON にして暗号化する形もこれ */
export const BankAccountText = z.object({
  bankCode: z.string().nullable(),
  bankName: z.string(),
  branchCode: z.string().nullable(),
  branchName: z.string(),
  accountType: BankAccountType,
  accountNo: z.string(),
  holder: z.string(),
});
export type BankAccountText = z.infer<typeof BankAccountText>;

/** 一覧・請求書の選択肢の1行。GET /api/v1/bank-accounts */
export const BankAccountRow = BankAccountText.extend({
  id: z.string().uuid(),
  isDefault: z.boolean(),
});
export type BankAccountRow = z.infer<typeof BankAccountRow>;

export const BankAccountsPage = z.object({
  accounts: z.array(BankAccountRow),
  /** 024 の1行の振込先が残っていればその文字（口座を1つ登録すると消える）。無ければ空 */
  legacyBankText: z.string(),
});
export type BankAccountsPage = z.infer<typeof BankAccountsPage>;

export const EMPTY_BANK: BankAccountText = { bankCode: null, bankName: "", branchCode: null, branchName: "", accountType: "ordinary", accountNo: "", holder: "" };

/**
 * 紙に印字する1行：「みずほ銀行（0001） 神保町支店（001） 普通 1234567 カ）ゲンバリョク」。コードが無ければ括弧を省く。
 * 銀行名が空なら空（振込先なし）
 */
export function bankAccountText(b: BankAccountText | null | undefined): string {
  if (!b || !b.bankName) return "";
  const bank = b.bankCode ? `${b.bankName}（${b.bankCode}）` : b.bankName;
  const branch = b.branchName ? (b.branchCode ? `${b.branchName}（${b.branchCode}）` : b.branchName) : "";
  return [bank, branch, BANK_TYPE_LABEL[b.accountType], b.accountNo, b.holder].filter(Boolean).join(" ");
}

/** コードの欄：""（空）は null＝コードなし、数字なら桁を確かめる（全角も直す）。省けば undefined（PUT では「変えない」） */
const codeField = (len: number, message: string) =>
  z
    .string()
    .transform((s, ctx) => {
      const d = digitsOnly(s);
      if (d === "") return null;
      if (!new RegExp(`^[0-9]{${len}}$`).test(d)) {
        ctx.addIssue({ code: "custom", message });
        return z.NEVER;
      }
      return d;
    })
    .optional();
const BankCode = codeField(4, BANK_MSG.bankCodeInvalid);
const BranchCode = codeField(3, BANK_MSG.branchCodeInvalid);

/** 追加 POST /bank-accounts。必須は 銀行名・支店名・口座番号・名義（Board と同じ）。コードは任意 */
export const BankAccountInput = z.object({
  bankCode: BankCode,
  bankName: z.string().trim().min(1, BANK_MSG.bankNameRequired).max(60),
  branchCode: BranchCode,
  branchName: z.string().trim().min(1, BANK_MSG.branchNameRequired).max(60),
  accountType: BankAccountType.default("ordinary"),
  accountNo: z
    .string()
    .trim()
    .transform((s) => digitsOnly(s))
    .pipe(z.string().min(1, BANK_MSG.accountNoRequired).max(20, BANK_MSG.accountNoInvalid)),
  holder: z.string().trim().min(1, BANK_MSG.holderRequired).max(100),
  /** 既定の口座にする（最初の1つは送らなくても既定になる） */
  isDefault: z.boolean().default(false),
});
export type BankAccountInput = z.infer<typeof BankAccountInput>;

/** 直す PUT /bank-accounts/:id。省いた欄は変えない（既定の値を入れないので partial() は使わない） */
export const BankAccountUpdate = z.object({
  bankCode: BankCode,
  bankName: z.string().trim().min(1, BANK_MSG.bankNameRequired).max(60).optional(),
  branchCode: BranchCode,
  branchName: z.string().trim().min(1, BANK_MSG.branchNameRequired).max(60).optional(),
  accountType: BankAccountType.optional(),
  accountNo: z
    .string()
    .transform((s) => digitsOnly(s))
    .pipe(z.string().min(1, BANK_MSG.accountNoRequired).max(20, BANK_MSG.accountNoInvalid))
    .optional(),
  holder: z.string().trim().min(1, BANK_MSG.holderRequired).max(100).optional(),
  isDefault: z.boolean().optional(),
});
export type BankAccountUpdate = z.infer<typeof BankAccountUpdate>;

/** 画面のフォームの形（文字のまま持つ。送るときに BankAccountInput が整える） */
export type BankAccountForm = { bankCode: string; bankName: string; branchCode: string; branchName: string; accountType: BankAccountType; accountNo: string; holder: string; isDefault: boolean };
export const EMPTY_BANK_FORM: BankAccountForm = { bankCode: "", bankName: "", branchCode: "", branchName: "", accountType: "ordinary", accountNo: "", holder: "", isDefault: false };
export const formOfRow = (r: BankAccountRow): BankAccountForm => ({ bankCode: r.bankCode ?? "", bankName: r.bankName, branchCode: r.branchCode ?? "", branchName: r.branchName, accountType: r.accountType, accountNo: r.accountNo, holder: r.holder, isDefault: r.isDefault });

/**
 * 024 の1行の振込先「○○銀行 ○○支店 普通 1234567 カ）○○」を欄に分ける手がかり（保存するまでは仮）。
 * 025 の画面から移した（Settings.tsx の splitLegacyBank）
 */
export function splitLegacyBank(text: string): BankAccountForm {
  const parts = text.trim().split(/[\s　]+/).filter(Boolean);
  const b: BankAccountForm = { ...EMPTY_BANK_FORM };
  if (parts.length < 2) return { ...b, bankName: text.trim() };
  b.bankName = parts[0]!;
  b.branchName = parts[1]!;
  const rest = parts.slice(2);
  const ti = rest.findIndex((p) => p === "普通" || p === "当座");
  if (ti >= 0) {
    b.accountType = rest[ti] === "当座" ? "current" : "ordinary";
    rest.splice(ti, 1);
  }
  const ni = rest.findIndex((p) => /^[0-9０-９]{4,10}$/.test(p));
  if (ni >= 0) {
    b.accountNo = digitsOnly(rest[ni]!);
    rest.splice(ni, 1);
  }
  b.holder = rest.join(" ");
  return b;
}

// ---------------------------------------------------------------------------
// 銀行・支店のマスタ（043 banks／bank_branches。zengin-code/source-data を月1回取り込む）
//   GET /api/v1/banks?q=          銀行を探す（コードの前方・名前・カナ・ひらがな。20件まで）
//   GET /api/v1/banks/:code/branches?q=   支店を探す（同じ。3桁ぴったりなら1件）
// ---------------------------------------------------------------------------

export const BankHit = z.object({
  code: z.string(),
  /** 全銀協の略称（「みずほ」） */
  name: z.string(),
  /** 紙に印字するフル名（「みずほ銀行」）。欄に入れるのはこちら */
  nameFull: z.string(),
  kana: z.string(),
});
export type BankHit = z.infer<typeof BankHit>;
export const BankSearch = z.object({ q: z.string(), items: z.array(BankHit) });
export type BankSearch = z.infer<typeof BankSearch>;

export const BranchHit = z.object({
  bankCode: z.string(),
  code: z.string(),
  name: z.string(),
  nameFull: z.string(),
  kana: z.string(),
});
export type BranchHit = z.infer<typeof BranchHit>;
export const BranchSearch = z.object({ bankCode: z.string(), q: z.string(), items: z.array(BranchHit) });
export type BranchSearch = z.infer<typeof BranchSearch>;

/** 検索の文字を整える：全角の数字は半角に、前後の空白を落とす。空なら "" */
export const normalizeBankQuery = (s: string): string => toHalf(s).trim().slice(0, 40);
